import { braintreeDeviceDataCorrelationOnly } from './braintree-device-data';

/**
 * Query-string / hash keys that the Venmo app-switch return URL can legitimately
 * carry. The `/venmo-return` trampoline mirrors these into sessionStorage
 * (because Angular's router has been observed to strip non-standard keys
 * mid-navigation), and the checkout page restores them into the live URL via
 * `history.replaceState` before the SDK reads `window.location`.
 *
 * Keep this list focused on values the Braintree Venmo SDK actually reads —
 * leaking unrelated query state across navigations is a UX/security smell.
 *
 * The SDK reads several of these (`venmoSuccess`, `venmoCancel`, `venmoError`,
 * `payment_method_nonce`, `username`, `id`, `resource_url`) when its
 * `hasTokenizationResult()` / `tokenize()` consumes the return URL.
 */
export const VENMO_RETURN_PARAM_KEYS = [
  'venmoReturn',
  'venmoSuccess',
  'venmoCancel',
  'venmoError',
  'payment_method_nonce',
  'paymentMethodNonce',
  'username',
  'id',
  'resource_id',
  'resource_url',
  'errorCode',
  'errorMessage',
] as const;

/**
 * The subset of {@link VENMO_RETURN_PARAM_KEYS} that the Braintree Venmo SDK
 * reads out of `window.location.hash` (NOT `window.location.search`) when its
 * `venmo.create()` → `hasTokenizationResult()` consumes the return URL.
 *
 * IMPORTANT: If these keys end up in the query string instead of the URL
 * fragment, the SDK will report `hasTokenizationResult() === false` and the
 * buyer will be stranded on the post-return spinner — exactly the
 * "DETECTION MISMATCH — URL vs SDK disagree" symptom we instrumented for.
 *
 * The trampoline component and `restoreVenmoReturnParamsToUrl()` use this
 * list to decide which keys belong in the URL fragment vs the query string.
 *
 * `venmoReturn` is INTENTIONALLY excluded — it's our internal marker, not
 * something the SDK consumes, so keeping it in the query keeps the URL
 * fragment focused on SDK-consumed values only.
 */
export const VENMO_SDK_HASH_PARAM_KEYS = [
  'venmoSuccess',
  'venmoCancel',
  'venmoError',
  'payment_method_nonce',
  'paymentMethodNonce',
  'username',
  'id',
  'resource_id',
  'resource_url',
  'errorCode',
  'errorMessage',
] as const;

export const VENMO_RETURN_PARAMS_SESSION_KEY = '__wayVenmoReturnParams__';

/**
 * sessionStorage key recording the most recently processed Venmo
 * `resource_id` plus a timestamp. Used by `VenmoReturnComponent` for
 * idempotency: if the Venmo deep link fires multiple times (observed with
 * the Venmo Sandbox Tester app's `mweb` channel), we skip re-processing
 * the same payment context within {@link VENMO_RETURN_DEDUPE_WINDOW_MS}.
 */
export const VENMO_RETURN_PROCESSED_SESSION_KEY = '__wayVenmoReturnProcessed__';

/**
 * Time window within which the same Venmo `resource_id` is considered a
 * duplicate deep-link fire and skipped. Picked to be long enough to cover
 * a slow page load + Braintree handshake, but short enough that a genuine
 * second attempt (user re-initiating Venmo a few minutes later) is not
 * incorrectly suppressed.
 */
export const VENMO_RETURN_DEDUPE_WINDOW_MS = 60_000;

export const VENMO_CHECKOUT_STATE_SESSION_KEY = '__wayVenmoCheckoutState__';

export const VENMO_CHECKOUT_STATE_TTL_MS = 10 * 60_000;


export function isVenmoEnabledByQueryParam(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('venmo') === 'true';
  } catch {
    return false;
  }
}

export function stashVenmoCheckoutState(state: unknown): void {
  if (typeof sessionStorage === 'undefined') return;
  if (state == null) return;
  try {
    sessionStorage.setItem(
      VENMO_CHECKOUT_STATE_SESSION_KEY,
      JSON.stringify({ stashedAt: Date.now(), state }),
    );
  } catch {
    // sessionStorage may be unavailable (quota, private mode)
  }
}

export function consumeVenmoCheckoutState<T = unknown>(): T | null {
  if (typeof sessionStorage === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(VENMO_CHECKOUT_STATE_SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    sessionStorage.removeItem(VENMO_CHECKOUT_STATE_SESSION_KEY);
  } catch {
    // Best-effort clear; proceed with whatever we read.
  }
  try {
    const parsed = JSON.parse(raw) as { stashedAt?: number; state?: T } | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.stashedAt !== 'number') return null;
    if (Date.now() - parsed.stashedAt > VENMO_CHECKOUT_STATE_TTL_MS) return null;
    return (parsed.state ?? null) as T | null;
  } catch {
    return null;
  }
}

/**
 * Pulls the previously-stashed Venmo return params out of sessionStorage
 * (without consuming them — use {@link consumeStashedVenmoReturnParams} for
 * that). Returns null if nothing is stashed or the value is malformed.
 */
export function readStashedVenmoReturnParams(): Record<string, string> | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(VENMO_RETURN_PARAMS_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const out: Record<string, string> = {};
    VENMO_RETURN_PARAM_KEYS.forEach((key) => {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.length > 0) {
        out[key] = value;
      }
    });
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}

/** Same as {@link readStashedVenmoReturnParams} but also clears the entry. */
export function consumeStashedVenmoReturnParams(): Record<string, string> | null {
  const params = readStashedVenmoReturnParams();
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(VENMO_RETURN_PARAMS_SESSION_KEY);
    } catch {
      // sessionStorage may be unavailable (e.g. private mode quotas)
    }
  }
  return params;
}

/**
 * Merges any stashed Venmo return params back into `window.location.search`
 * via `history.replaceState` (so the live URL — which the Braintree Venmo SDK
 * reads in `venmo.create()` / `hasTokenizationResult()` — regains
 * `payment_method_nonce`, `venmoSuccess`, etc. that Angular's router may have
 * stripped during the trampoline navigation). Returns the params that were
 * restored, or null if nothing was stashed / running outside a browser.
 *
 * NOTE: This MUST run before `BraintreeWalletManager.init()` is called on the
 * destination page, otherwise the Venmo SDK reads the stripped URL and
 * `hasTokenizationResult()` reports false — leaving the buyer stuck on the
 * post-return spinner.
 */
export function restoreVenmoReturnParamsToUrl(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  const stashed = consumeStashedVenmoReturnParams();
  if (!stashed) return null;
  try {
    const url = new URL(window.location.href);
    const hashKeySet = new Set<string>(VENMO_SDK_HASH_PARAM_KEYS);

    // Parse the live hash so we can union the stashed SDK keys back into
    // it without clobbering anything that survived navigation.
    const liveHashRaw = url.hash || '';
    const liveHashBody = liveHashRaw.startsWith('#') ? liveHashRaw.slice(1) : liveHashRaw;
    const hashParams = new URLSearchParams(liveHashBody);

    let queryChanged = false;
    let hashChanged = false;

    Object.entries(stashed).forEach(([key, value]) => {
      if (hashKeySet.has(key)) {
        // SDK-consumed key — must end up in the fragment, not the query,
        // or `hasTokenizationResult()` will silently return false.
        if (!hashParams.has(key)) {
          hashParams.set(key, value);
          hashChanged = true;
        }
      } else if (!url.searchParams.has(key)) {
        // Trampoline-only key (e.g. `venmoReturn=1`) — query is fine.
        url.searchParams.set(key, value);
        queryChanged = true;
      }
    });

    if (!queryChanged && !hashChanged) return stashed;

    const newHashSerialized = hashParams.toString();
    const newRelative =
      url.pathname +
      (url.search ? url.search : '') +
      (newHashSerialized ? `#${newHashSerialized}` : '');
    window.history.replaceState(window.history.state, '', newRelative);
    return stashed;
  } catch {
    return stashed;
  }
}

/**
 * Returns true if the same Venmo `resource_id` was successfully processed
 * within the last {@link VENMO_RETURN_DEDUPE_WINDOW_MS}. Used by the
 * trampoline to drop duplicate deep-link fires (which we've observed from
 * the Venmo Sandbox Tester app's `mweb` channel).
 *
 * Returns false on any error or in non-browser environments so that the
 * caller never silently swallows a legitimate return.
 */
export function wasVenmoReturnProcessedRecently(resourceId: string | undefined | null): boolean {
  if (!resourceId) return false;
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(VENMO_RETURN_PROCESSED_SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { resourceId?: string; processedAt?: number } | null;
    if (!parsed || typeof parsed !== 'object') return false;
    if (parsed.resourceId !== resourceId) return false;
    if (typeof parsed.processedAt !== 'number') return false;
    return Date.now() - parsed.processedAt < VENMO_RETURN_DEDUPE_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * Records that the given Venmo `resource_id` has been processed so that
 * {@link wasVenmoReturnProcessedRecently} will short-circuit any duplicate
 * deep-link fires that follow within the dedupe window.
 *
 * Best-effort — failures (quota exceeded, private mode) are ignored.
 */
export function markVenmoReturnProcessed(resourceId: string | undefined | null): void {
  if (!resourceId) return;
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      VENMO_RETURN_PROCESSED_SESSION_KEY,
      JSON.stringify({ resourceId, processedAt: Date.now() }),
    );
  } catch {
    // sessionStorage may be unavailable (quota, private mode)
  }
}

/**
 * Builds a return URL pointing to the `/venmo-return` trampoline route.
 *
 * The trampoline path is intentionally NOT claimed by the Way iOS/Android
 * app's Universal Links / App Links configuration. This prevents the OS
 * from intercepting the Venmo return and opening the native Way app
 * instead of the browser tab where checkout is in progress — the exact
 * same problem `/paypal-return` was created to solve.
 */
export function buildVenmoTrampolineReturnUrl(returnPath?: string): string {
  if (typeof window === 'undefined') return '';
  const path = returnPath || window.location.pathname || '/checkoutGuest';
  try {
    const url = new URL('/venmo-return', window.location.origin);
    url.searchParams.set('returnPath', path);
    url.searchParams.set('venmoReturn', '1');
    return url.toString();
  } catch {
    return `${window.location.origin}/venmo-return?returnPath=${encodeURIComponent(path)}&venmoReturn=1`;
  }
}

export interface BraintreeVenmoResult {
  nonce: string;
  paymentOption: 'VENMO';
  deviceData?: string;
  username?: string;
}

export interface BraintreeVenmoConfig {
  authorization: string;
  allowDesktop?: boolean;
  allowDesktopWebLogin?: boolean;
  mobileWebFallBack?: boolean;
  paymentMethodUsage?: 'single_use' | 'multi_use';
  /** URL the Venmo iOS app opens to return from an app switch. Defaults to window.location.href. */
  deepLinkReturnUrl?: string;
  /** When false, isBrowserSupported() only returns true for browsers that support same-tab return (iOS Safari, Android Chrome). Defaults to false for SPA compatibility. */
  allowNewBrowserTab?: boolean;
  /** Use window.location.href redirect instead of window.open for iOS webviews/Safari View Controller. */
  useRedirectForIOS?: boolean;
}

export class BraintreeVenmo {
  private clientInstance: any = null;
  private venmoInstance: any = null;
  private dataCollectorInstance: any = null;
  private deviceData: string | undefined;
  private destroyed = false;
  private browserSupported = false;
  private desktopWebLoginEnabled = false;
  /**
   * True between the moment `tokenize()` is called and the moment it
   * settles (resolved or rejected). Used by `BraintreeWalletManager` to
   * skip silent re-init that would tear down the SDK mid-Venmo-flow and
   * produce `METHOD_CALLED_AFTER_TEARDOWN` errors on app-switch return.
   */
  private inFlight = false;

  /** Returns true if a `tokenize()` call is currently in flight. */
  isInFlight(): boolean {
    return this.inFlight;
  }

  /** Returns true if `teardown()` has been invoked on this instance. */
  isDestroyed(): boolean {
    return this.destroyed;
  }

  async init(config: BraintreeVenmoConfig): Promise<void> {
    let braintree: any;
    try {
      const mod: any = await import('braintree-web');
      braintree = mod.default ?? mod;
    } catch {
      return;
    }
    if (this.destroyed) return;

    try {
      this.clientInstance = await new Promise<any>((resolve, reject) => {
        braintree.client.create(
          { authorization: config.authorization },
          (err: any, instance: any) => (err ? reject(err) : resolve(instance)),
        );
      });
    } catch {
      return;
    }
    if (this.destroyed) return;

    try {
      if (braintree.dataCollector?.create) {
        const dc = await this.createDataCollector(braintree, this.clientInstance);
        if (dc) {
          this.dataCollectorInstance = dc;
          this.deviceData = braintreeDeviceDataCorrelationOnly(dc.deviceData);
        }
      }
    } catch {
      // device data is optional
    }
    if (this.destroyed) return;

    if (!braintree.venmo?.create) {
      return;
    }

    const venmoOptions: Record<string, unknown> = {
      client: this.clientInstance,
      allowDesktop: config.allowDesktop ?? true,
      allowDesktopWebLogin: config.allowDesktopWebLogin ?? true,
      mobileWebFallBack: config.mobileWebFallBack ?? true,
      paymentMethodUsage: config.paymentMethodUsage || 'single_use',
      allowNewBrowserTab: config.allowNewBrowserTab ?? false,
    };

    if (config.deepLinkReturnUrl) {
      venmoOptions['deepLinkReturnUrl'] = config.deepLinkReturnUrl;
    } else if (typeof window !== 'undefined') {
      venmoOptions['deepLinkReturnUrl'] = window.location.href;
    }

    if (config.useRedirectForIOS !== undefined) {
      venmoOptions['useRedirectForIOS'] = config.useRedirectForIOS;
    }

    try {
      this.venmoInstance = await new Promise<any>((resolve, reject) => {
        braintree.venmo.create(
          venmoOptions,
          (err: any, instance: any) => (err ? reject(err) : resolve(instance)),
        );
      });
    } catch {
      return;
    }
    this.browserSupported = !!this.venmoInstance?.isBrowserSupported?.();
    this.desktopWebLoginEnabled = !!(venmoOptions['allowDesktopWebLogin']);
  }

  isAvailable(): boolean {
    return !!this.venmoInstance && (this.browserSupported || this.desktopWebLoginEnabled);
  }

  /**
   * Returns true if there is already a tokenization result waiting
   * (e.g. the page was relaunched in a new tab after Venmo app approval).
   */
  hasTokenizationResult(): boolean {
    return !!this.venmoInstance?.hasTokenizationResult?.();
  }

  async tokenize(): Promise<BraintreeVenmoResult> {
    if (!this.venmoInstance) {
      throw new Error('Venmo not initialized');
    }

    const detachReturnListener = this.installVenmoReturnListener();
    this.inFlight = true;

    try {
      const payload = await new Promise<any>((resolve, reject) => {
        this.venmoInstance.tokenize((err: any, result: any) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      });

      return {
        nonce: payload.nonce,
        paymentOption: 'VENMO',
        deviceData: this.deviceData,
        username: payload.details?.username,
      };
    } finally {
      this.inFlight = false;
      detachReturnListener();
    }
  }

  /**
   * Pre-arms a visibility/pageshow listener that kicks off Braintree's payment-
   * context polling when the page becomes visible again after the user clicks
   * Venmo. See {@link tokenize} for why the SDK's own listener is unreliable on
   * Android Chrome with the Venmo Universal Link.
   *
   * The returned function detaches the listener and must be called once the
   * SDK's `tokenize()` settles, regardless of outcome.
   */
  private installVenmoReturnListener(): () => void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return () => {
        // no-op in non-browser environments (SSR)
      };
    }

    const venmoInstance = this.venmoInstance;
    if (!venmoInstance) {
      return () => {
        // no-op when there is nothing to poll against
      };
    }

    // The fallback only applies to the mobile-with-hash-change-listeners flow.
    // The desktop QR flow and desktop-web-login flow have their own popup-driven
    // resolution paths that already work reliably — hijacking their
    // `_tokenizePromise` via `processHashChangeFlowResults()` would race against
    // those paths and could resolve with the wrong shape.
    const instanceInternals = venmoInstance as {
      _useDesktopQRFlow?: boolean;
      _useAllowDesktopWebLogin?: boolean;
      _cannotHaveReturnUrls?: boolean;
    };
    if (
      instanceInternals._useDesktopQRFlow ||
      instanceInternals._useAllowDesktopWebLogin ||
      instanceInternals._cannotHaveReturnUrls
    ) {
      return () => {
        // no-op for desktop / manual-return flows
      };
    }

    // Ignore visibility events that fire within the first ~750ms after we attach.
    // The user just tapped the Venmo button; any visibility flips happening
    // before the OS actually app-switches to Venmo would be false positives.
    const armedAt = Date.now();
    const VISIBILITY_GRACE_MS = 750;
    // Delay before invoking processHashChangeFlowResults() — gives the OS a
    // moment to fully restore the tab and lets the SDK's own listener (if it
    // somehow did register) fire first.
    const PROCESS_DELAY_MS = 1000;

    let processingTriggered = false;
    let detached = false;
    let pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const wrapperRef = this;

    const triggerProcessing = () => {
      if (processingTriggered || detached) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      // If the wrapper was torn down externally (e.g., Angular destroyed the
      // host component while the user was still on the Venmo app), calling
      // ANY method on the inner SDK instance produces `METHOD_CALLED_AFTER_
      // TEARDOWN`. Detect that condition first and short-circuit cleanly —
      // tokenize()'s outer promise will already have rejected via the SDK's
      // own teardown handling.
      if (wrapperRef.destroyed) {
        processingTriggered = true;
        return;
      }
      // If the SDK already resolved (e.g., on a desktop browser where its own
      // listener works), `_tokenizePromise` is deleted by the SDK's
      // `completeFlow`. Nothing for us to do.
      const internalPromise = (venmoInstance as { _tokenizePromise?: { resolve: (v: unknown) => void; reject: (e: unknown) => void } })._tokenizePromise;
      if (!internalPromise) return;

      processingTriggered = true;

      Promise.resolve()
        .then(() => (venmoInstance as { processHashChangeFlowResults?: (hash?: string) => Promise<unknown> }).processHashChangeFlowResults?.())
        .then((res) => {
          const stillPending = (venmoInstance as { _tokenizePromise?: { resolve: (v: unknown) => void } })._tokenizePromise;
          if (!stillPending) return;
          stillPending.resolve(res);
          delete (venmoInstance as { _tokenizePromise?: unknown })._tokenizePromise;
        })
        .catch((err: { code?: string; message?: string }) => {
          const stillPending = (venmoInstance as { _tokenizePromise?: { reject: (e: unknown) => void } })._tokenizePromise;
          if (!stillPending) return;
          // Polling may still be in flight (CREATED status keeps polling) and
          // only rejects on EXPIRED/FAILED/CANCELED/network error. Reset our
          // trigger flag so the next visibility flip can try again — useful
          // when the user backgrounds and foregrounds the tab multiple times.
          if (err?.code === 'VENMO_MOBILE_POLLING_TOKENIZATION_NETWORK_ERROR') {
            processingTriggered = false;
            return;
          }
          // `METHOD_CALLED_AFTER_TEARDOWN` here means teardown raced our
          // invocation: the wrapper looked alive when we checked above, but
          // the inner SDK was already gone by the time the microtask ran.
          // No point rejecting the outer promise with this — the teardown
          // path is the canonical owner of cleanup.
          if (err?.code === 'METHOD_CALLED_AFTER_TEARDOWN') {
            return;
          }
          stillPending.reject(err);
          delete (venmoInstance as { _tokenizePromise?: unknown })._tokenizePromise;
        });
    };

    const scheduleTrigger = (_source: 'visibilitychange' | 'pageshow') => {
      if (detached) return;
      if (Date.now() - armedAt < VISIBILITY_GRACE_MS) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      if (pendingTimeoutId !== null) return;
      pendingTimeoutId = setTimeout(() => {
        pendingTimeoutId = null;
        triggerProcessing();
      }, PROCESS_DELAY_MS);
    };

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        scheduleTrigger('visibilitychange');
      }
    };
    const onPageShow = () => {
      scheduleTrigger('pageshow');
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      detached = true;
      if (pendingTimeoutId !== null) {
        clearTimeout(pendingTimeoutId);
        pendingTimeoutId = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }

  getDeviceData(): string | undefined {
    return this.deviceData;
  }

  private createDataCollector(braintree: any, clientInstance: any): Promise<any> {
    return new Promise((resolve) => {
      const tryCreate = (opts: Record<string, unknown>, fallback: () => void) => {
        try {
          braintree.dataCollector.create(opts, (err: any, dc: any) => {
            if (!err && dc) {
              resolve(dc);
              return;
            }
            fallback();
          });
        } catch {
          fallback();
        }
      };
      tryCreate(
        { client: clientInstance, kount: true, paypal: true },
        () => tryCreate({ client: clientInstance, paypal: true }, () => resolve(null)),
      );
    });
  }

  async teardown(): Promise<void> {
    this.destroyed = true;
    try {
      this.venmoInstance?.teardown?.(() => {});
    } catch {
      // ignore
    }
    try {
      this.dataCollectorInstance?.teardown?.(() => {});
    } catch {
      // ignore
    }
    this.venmoInstance = null;
    this.clientInstance = null;
    this.dataCollectorInstance = null;
  }
}
